import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { AppService } from '../../../app.service';
import { Location } from '@angular/common';
import { UtilsService } from '../../../shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UsersSelectorComponent } from '../../users/users-selector/users-selector.component';

@Component({
  selector: 'app-accounts-filter',
  templateUrl: './accounts-filter.component.html',
  styleUrls: ['./accounts-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountsFilterComponent implements OnInit {
  // Define the filter options.
  filter_options: {
    subscription: string;
    account_type: string;
    sms_notifications: string;
    associated_users: { id: number; name: string }[];
    include_merged_accounts: boolean;
    include_personal_accounts: boolean;
  } = {
    subscription: '',
    account_type: '',
    sms_notifications: '',
    associated_users: [],
    include_merged_accounts: false,
    // Personal accounts are excluded by default (SC-301). They are named after the person who owns them,
    // so once approvals are in use they would fill this list with entries that read as people pretending
    // to be companies, and none of them is an account anybody administers.
    include_personal_accounts: false
  };

  constructor(
    public app: AppService,
    public location: Location,
    public utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AccountsFilterComponent>
  ) {
    // Set default subscription of an empty string if none is provided.
    if (typeof this.data['subscription'] == 'undefined') {
      this.data['subscription'] = '';
    }

    // Set a default account type of an empty string (All) if none is provided.
    if (typeof this.data['account_type'] == 'undefined') {
      this.data['account_type'] = '';
    }

    // Set a default SMS notification state of an empty string (All) if none is provided.
    if (typeof this.data['sms_notifications'] == 'undefined') {
      this.data['sms_notifications'] = '';
    }

    // Set a default empty associated users selection if none is provided.
    if (!Array.isArray(this.data['associated_users'])) {
      this.data['associated_users'] = [];
    }

    // Set a default value of false for including merged accounts.
    if (typeof this.data['include_merged_accounts'] == 'undefined') {
      this.data['include_merged_accounts'] = false;
    }

    // Set a default value of false for including personal accounts.
    if (typeof this.data['include_personal_accounts'] == 'undefined') {
      this.data['include_personal_accounts'] = false;
    }
  }

  ngOnInit() {
    // The constructor makes sure the values are set and present.
    this.filter_options.subscription = this.data['subscription'];
    this.filter_options.account_type = this.data['account_type'];
    this.filter_options.sms_notifications = this.data['sms_notifications'];
    this.filter_options.associated_users = this.data['associated_users'];
    this.filter_options.include_merged_accounts = this.data[
      'include_merged_accounts'
    ];
    this.filter_options.include_personal_accounts = this.data[
      'include_personal_accounts'
    ];
  }

  /**
   * Open the users selector to choose which associated user(s) to filter accounts by.
   */
  onSelectAssociatedUsers() {
    this.utils.showComponentDialog(
      UsersSelectorComponent,
      {
        multiple: true,
        returnObjects: true,
        selected: this.filter_options.associated_users
      },
      { width: '1200px' },
      (users: any[]) => {
        if (!users) {
          return;
        }
        this.filter_options.associated_users = (users || []).map((u: any) => ({
          id: Number(u.id),
          name: u.contact_person || u.email || '#' + u.id
        }));
      }
    );
  }

  /**
   * Clear the filter options and send the response back.
   */
  onClearAndClose() {
    // Clear all filter options.
    this.filter_options.subscription = '';
    this.filter_options.account_type = '';
    this.filter_options.sms_notifications = '';
    this.filter_options.associated_users = [];
    this.filter_options.include_merged_accounts = false;
    // Clearing the filter returns personal accounts to being EXCLUDED, not shown. "Clear" here means
    // back to the default view, and the default view deliberately hides them.
    this.filter_options.include_personal_accounts = false;

    this.onSaveAndClose();
  }

  /**
   * Close the dialog and send the selected options as the response.
   */
  onSaveAndClose() {
    this.dialogRef.close(this.filter_options);
  }
}
